import { useTranslation } from "react-i18next";
import { PageSEO } from "@/components/seo/PageSEO";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Route, ExternalLink } from "lucide-react";
import { MiniProductAnimation } from "@/components/home/MiniProductAnimations";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";

const sisterCompanies = [
  {
    key: "postnummerservice" as const,
    icon: MapPin,
    href: "https://www.postnummerservice.se",
    domain: "postnummerservice.se",
  },
  {
    key: "ecoroute" as const,
    icon: Route,
    href: "https://www.ecoroute.com",
    domain: "ecoroute.com",
  },
];

const products = [
  {
    key: "atlas" as const,
    href: "/tjanster/atlas",
  },
  {
    key: "valid" as const,
    href: "/tjanster/valid",
  },
  {
    key: "adresstwatt" as const,
    href: "/tjanster/adresstwatt",
  },
];

const Produkter = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  return (
    <div>
      <PageSEO title={t("nav.produkter")} description={t("seo.produkter")} />
      {/* Hero */}
      <section className="bg-primary text-primary-foreground py-20 lg:py-28">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-heading leading-tight tracking-tight">
              {t("products.title")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-primary-foreground/80 font-body leading-relaxed">
              {t("products.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Cards */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={getLocalizedPath(product.href)}
                  className="group block bg-card rounded-2xl overflow-hidden shadow-sm border border-border hover:shadow-xl hover:border-primary/20 transition-all duration-300 h-full"
                >
                  {/* Animation illustration */}
                  <div className="bg-primary/[0.03] h-52 lg:h-56 overflow-hidden flex items-center justify-center p-6 border-b border-border group-hover:bg-primary/[0.06] transition-colors duration-300">
                    <MiniProductAnimation productKey={product.key} />
                  </div>

                  {/* Content */}
                  <div className="p-8 lg:p-10">
                    <h2 className="text-3xl font-medium font-heading text-primary mb-3">
                      {t(`products.${product.key}.title`)}
                    </h2>
                    <p className="text-muted-foreground font-body leading-relaxed mb-6">
                      {t(`products.${product.key}.description`)}
                    </p>
                    <span className="inline-flex items-center text-sm font-bold font-subheading text-coral group-hover:gap-2.5 transition-all">
                      {t(`products.${product.key}.cta`)}
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sister companies */}
      <section className="py-20 lg:py-28 bg-secondary/50">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center mb-12 lg:mb-16"
          >
            <span className="inline-block text-sm font-bold font-subheading uppercase tracking-widest text-coral mb-4">
              {t("products.sisterCompanies.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              {t("products.sisterCompanies.title")}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-body leading-relaxed">
              {t("products.sisterCompanies.subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {sisterCompanies.map((company, index) => (
              <motion.div
                key={company.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <a
                  href={company.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-card rounded-2xl border border-border shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 h-full p-8 lg:p-10"
                  aria-label={`${t(`products.sisterCompanies.${company.key}.cta`)} (öppnas i nytt fönster)`}
                >
                  <company.icon
                    className="h-8 w-8 text-primary dark:text-white mb-6"
                    strokeWidth={1.75}
                  />
                  <h3 className="text-2xl font-bold font-subheading text-foreground mb-3">
                    {t(`products.sisterCompanies.${company.key}.name`)}
                  </h3>
                  <p className="text-muted-foreground font-body leading-relaxed mb-6">
                    {t(`products.sisterCompanies.${company.key}.description`)}
                  </p>
                  <span className="inline-flex items-center text-sm font-bold font-subheading text-coral group-hover:gap-2.5 transition-all">
                    {t(`products.sisterCompanies.${company.key}.cta`)}
                    <ExternalLink className="ml-1.5 h-4 w-4" aria-hidden="true" />
                  </span>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-background border-t border-border">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              {t("productPages.ctaTitle")}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-body">
              {t("productPages.ctaSubtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                asChild
                className="bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg shadow-coral/25 text-base px-8"
              >
                <Link to={getLocalizedPath("/kontakt?type=demo")}>
                  {t("productPages.ctaButton")}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-medium text-base">
                <Link to={getLocalizedPath("/kontakt")}>{t("productPages.ctaSecondary")}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Produkter;
